<template>
  <div class="min-h-screen bg-gradient-to-br from-violet-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
    <div class="w-full max-w-5xl">
      <!-- Header -->
      <div class="text-center mb-6">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">
          Selecciona tu Negocio
        </h1>
        <p class="text-sm text-gray-600 dark:text-gray-300">
          Elige el negocio con el que deseas trabajar en esta sesión
        </p>
      </div>

      <!-- Super Admin Options -->
      <div v-if="userStore.isSuper" class="mb-6">
        <h2 class="text-base font-semibold text-gray-900 dark:text-white mb-3 text-center">
          Opciones de Administrador
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl mx-auto">
          <!-- Manage Users Card -->
          <div
            @click="goToUserManagement"
            class="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer transform hover:scale-[1.02] overflow-hidden group"
          >
            <div class="p-5 text-white">
              <div class="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl mx-auto mb-3">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                </svg>
              </div>
              <h3 class="text-base font-semibold text-center mb-1">Gestionar Usuarios</h3>
              <p class="text-center text-xs opacity-90">Administrar usuarios del sistema</p>
            </div>
          </div>

          <!-- Manage Businesses Card -->
          <div
            @click="goToBusinessManagement"
            class="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer transform hover:scale-[1.02] overflow-hidden group"
          >
            <div class="p-5 text-white">
              <div class="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl mx-auto mb-3">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 7h10M7 11h10M7 15h10"/>
                </svg>
              </div>
              <h3 class="text-base font-semibold text-center mb-1">Gestionar Negocios</h3>
              <p class="text-center text-xs opacity-90">Administrar negocios del sistema</p>
            </div>
          </div>
        </div>

        <div class="mt-6 border-t border-gray-200 dark:border-gray-700 pt-5">
          <h2 class="text-base font-semibold text-gray-900 dark:text-white mb-3 text-center">
            Seleccionar Negocio para Operar
          </h2>
        </div>
      </div>

      <!-- Business Selection Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="negocio in userStore.negocios"
          :key="negocio.id"
          @click="selectBusiness(negocio)"
          class="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer transform hover:scale-[1.02] border border-gray-200 dark:border-gray-700 overflow-hidden group"
        >
          <div class="p-4">
            <div class="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-violet-500 to-blue-500 rounded-xl mx-auto mb-3 group-hover:from-violet-600 group-hover:to-blue-600 transition-all">
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 7h10M7 11h10M7 15h10"/>
              </svg>
            </div>

            <h3 class="text-base font-semibold text-gray-900 dark:text-white text-center mb-1">
              {{ negocio.nombre }}
            </h3>

            <p v-if="negocio.descripcion" class="text-gray-600 dark:text-gray-300 text-center text-xs mb-3 line-clamp-2">
              {{ negocio.descripcion }}
            </p>

            <div class="flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
                </svg>
                ID: {{ negocio.id }}
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-r from-violet-500 to-blue-500 group-hover:from-violet-600 group-hover:to-blue-600 transition-all py-2.5 text-center">
            <span class="text-white text-sm font-medium">Seleccionar</span>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <EmptyState
        v-if="userStore.negocios.length === 0"
        title="No hay negocios disponibles"
        description="Contacta al administrador para obtener acceso a un negocio"
        size="md"
      >
        <template #icon>
          <svg class="w-16 h-16 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 7h10M7 11h10M7 15h10"/>
          </svg>
        </template>
      </EmptyState>
    </div>
  </div>
</template>

<script setup>
import { useUserStore } from '@/stores/user'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import EmptyState from '@/components/base/EmptyState.vue'

const userStore = useUserStore()
const router = useRouter()

const selectBusiness = async (negocio) => {
  const result = await Swal.fire({
    title: '¿Confirmar selección?',
    text: `¿Deseas trabajar con el negocio "${negocio.nombre}"?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#8B5CF6',
    cancelButtonColor: '#6B7280',
    confirmButtonText: 'Sí, seleccionar',
    cancelButtonText: 'Cancelar',
    reverseButtons: true
  })

  if (result.isConfirmed) {
    userStore.seleccionarNegocio(negocio)
    
    await Swal.fire({
      title: '¡Negocio seleccionado!',
      text: `Ahora trabajarás con "${negocio.nombre}"`,
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    })
    
    // Redirect to dashboard after selection
    router.push('/dashboard')
  }
}

const goToUserManagement = () => {
  router.push('/admin/users')
}

const goToBusinessManagement = () => {
  router.push('/admin/negocios')
}
</script>